// src/admin/ThemeSettings.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Save, Palette, RotateCcw, Eye, Check, AlertCircle, Sparkles
} from 'lucide-react';

const ThemeSettings = () => {
  // PINK HEAVY NAVIROSE Theme
  const defaultTheme = {
    bodyBg: '#FFFFFF',
    cardBg: '#FFFFFF',
    headerBg: '#FFFFFF',
    footerBg: '#071B36',
    topBarBg: '#071B36',
    primaryText: '#9B3F5B',
    secondaryText: '#DC829A',
    headerText: '#9B3F5B',
    footerText: '#F5AEC0',
    topBarText: '#F5AEC0',
    buttonBg: '#F5AEC0',
    buttonText: '#FFFFFF',
    buttonHoverBg: '#DC829A',
    accentColor: '#DC829A',
    linkColor: '#DC829A',
    badgeColor: '#DC829A',
    borderColor: '#F5AEC0',
    bottomNavBg: '#FFFFFF',
    bottomNavActiveBg: '#F5AEC0',
    bottomNavActiveText: '#FFFFFF',
    bottomNavInactiveText: '#DC829A',
  };

  const presets = {
    pinkHeavy: {
      name: 'NAVIROSE Pink',
      colors: { ...defaultTheme },
    },
    organic: {
      name: 'Organic Green',
      colors: {
        bodyBg: '#F9FAFB',
        cardBg: '#FFFFFF',
        headerBg: '#FFFFFF',
        footerBg: '#14532D',
        topBarBg: '#14532D',
        primaryText: '#1F2937',
        secondaryText: '#6B7280',
        headerText: '#14532D',
        footerText: '#FFFFFF',
        topBarText: '#FFFFFF',
        buttonBg: '#15803D',
        buttonText: '#FFFFFF',
        buttonHoverBg: '#166534',
        accentColor: '#16A34A',
        linkColor: '#15803D',
        badgeColor: '#EF4444',
        borderColor: '#E5E7EB',
        bottomNavBg: '#FFFFFF',
        bottomNavActiveBg: '#1F2937',
        bottomNavActiveText: '#FFFFFF',
        bottomNavInactiveText: '#4B5563',
      },
    },
    dark: {
      name: 'Dark Elegance',
      colors: {
        bodyBg: '#0F172A',
        cardBg: '#1E293B',
        headerBg: '#1E293B',
        footerBg: '#0F172A',
        topBarBg: '#0F172A',
        primaryText: '#F1F5F9',
        secondaryText: '#94A3B8',
        headerText: '#F1F5F9',
        footerText: '#F1F5F9',
        topBarText: '#F1F5F9',
        buttonBg: '#3B82F6',
        buttonText: '#FFFFFF',
        buttonHoverBg: '#2563EB',
        accentColor: '#3B82F6',
        linkColor: '#60A5FA',
        badgeColor: '#EF4444',
        borderColor: '#334155',
        bottomNavBg: '#1E293B',
        bottomNavActiveBg: '#3B82F6',
        bottomNavActiveText: '#FFFFFF',
        bottomNavInactiveText: '#94A3B8',
      },
    },
  };

  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const docRef = doc(db, 'settings', 'theme');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setTheme({ ...defaultTheme, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error fetching theme:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTheme();
  }, []);

  // ✅ Force apply theme to DOM
  const applyThemeToDOM = (themeData) => {
    const root = document.documentElement;
    const cssVarMap = {
      bodyBg: '--color-body-bg',
      cardBg: '--color-card-bg',
      headerBg: '--color-header-bg',
      footerBg: '--color-footer-bg',
      topBarBg: '--color-topbar-bg',
      primaryText: '--color-primary-text',
      secondaryText: '--color-secondary-text',
      headerText: '--color-header-text',
      footerText: '--color-footer-text',
      topBarText: '--color-topbar-text',
      buttonBg: '--color-button-bg',
      buttonText: '--color-button-text',
      buttonHoverBg: '--color-button-hover-bg',
      accentColor: '--color-accent',
      linkColor: '--color-link',
      badgeColor: '--color-badge',
      borderColor: '--color-border',
      bottomNavBg: '--color-bottomnav-bg',
      bottomNavActiveBg: '--color-bottomnav-active-bg',
      bottomNavActiveText: '--color-bottomnav-active-text',
      bottomNavInactiveText: '--color-bottomnav-inactive-text',
    };

    Object.entries(cssVarMap).forEach(([key, cssVar]) => {
      if (themeData[key]) {
        root.style.setProperty(cssVar, themeData[key]);
      }
    });

    if (themeData.bodyBg) document.body.style.backgroundColor = themeData.bodyBg;
    if (themeData.primaryText) document.body.style.color = themeData.primaryText;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // 1. Save to Firestore
      await setDoc(doc(db, 'settings', 'theme'), theme, { merge: true });

      // 2. Save to localStorage (instant load ke liye)
      localStorage.setItem('siteTheme', JSON.stringify(theme));

      // 3. ✅ Force apply to DOM immediately
      applyThemeToDOM(theme);

      // 4. Dispatch event for other tabs/components
      window.dispatchEvent(new Event('themeUpdated'));

      setMessage({ type: 'success', text: 'Theme saved! Website par apply ho gaya.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save theme.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Reset to NAVIROSE Pink default?')) return;
    setTheme(defaultTheme);
    applyThemeToDOM(defaultTheme);
    setMessage({ type: 'success', text: 'Reset to Pink default. Click Save to apply.' });
  };

  const applyPreset = (presetKey) => {
    if (!window.confirm(`Apply "${presets[presetKey].name}" theme?`)) return;
    setTheme({ ...presets[presetKey].colors });
    setMessage({ type: 'success', text: `${presets[presetKey].name} loaded. Click Save to apply.` });
  };

  // ✅ Preview for color input that updates theme state AND preview instantly
  const handleColorChange = (field, value) => {
    const updatedTheme = { ...theme, [field]: value };
    setTheme(updatedTheme);

    // Also apply to DOM for live preview
    applyThemeToDOM(updatedTheme);
  };

  const ColorInput = ({ label, field, description }) => (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={theme[field] || '#FFFFFF'}
          onChange={(e) => handleColorChange(field, e.target.value)}
          className="w-14 h-11 rounded-lg border-2 border-gray-300 cursor-pointer"
        />
        <input
          type="text"
          value={theme[field] || ''}
          onChange={(e) => handleColorChange(field, e.target.value)}
          className="flex-1 border border-gray-300 rounded-lg py-2.5 px-3 text-sm font-mono uppercase"
        />
      </div>
      {description && (
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-pink-400 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading theme...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Palette className="text-pink-500" size={28} /> Website Theme
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Poori website ke colors yahan se change karein. Live preview bhi milega.
          </p>
        </div>
        <button onClick={handleReset}
          className="border border-gray-300 text-gray-700 px-5 py-3 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 font-semibold">
          <RotateCcw size={18} /> Reset NAVIROSE Pink
        </button>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-pink-50 text-pink-700 border border-pink-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Preset Themes */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Sparkles size={20} className="text-pink-500" /> Quick Preset Themes
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(presets).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className="p-4 rounded-xl border-2 border-gray-200 hover:border-pink-400 transition text-left"
            >
              <div className="flex gap-1 mb-2">
                {[preset.colors.bodyBg, preset.colors.buttonBg, preset.colors.footerBg, preset.colors.accentColor].map((c, i) => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-white shadow-md" style={{ backgroundColor: c }}></div>
                ))}
              </div>
              <p className="font-semibold text-sm text-gray-800">{preset.name}</p>
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">

        {/* Live Preview */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Eye size={20} className="text-pink-500" /> Live Preview
          </h2>
          <div className="rounded-xl overflow-hidden border-2 border-gray-200"
            style={{ backgroundColor: theme.bodyBg }}>
            <div className="px-4 py-2 text-xs" style={{ backgroundColor: theme.topBarBg, color: theme.topBarText }}>
              📞 +92 300 1234567 &nbsp; ✉️ hello@navirose.com
            </div>

            <div className="px-4 py-3 flex justify-between items-center border-b"
              style={{ backgroundColor: theme.headerBg, borderColor: theme.borderColor }}>
              <span className="font-bold text-lg" style={{ color: theme.headerText }}>NAVIROSE</span>
              <div className="flex gap-2 text-sm" style={{ color: theme.primaryText }}>
                <span>Home</span>
                <span>Shop</span>
                <span>About</span>
              </div>
            </div>

            <div className="p-4 space-y-3" style={{ backgroundColor: theme.bodyBg }}>
              <h3 className="font-bold text-xl" style={{ color: theme.primaryText }}>
                Fresh Organic Products
              </h3>
              <p className="text-sm" style={{ color: theme.secondaryText }}>
                Premium quality fresh organic products.
              </p>

              <div className="p-3 rounded-lg border" style={{ backgroundColor: theme.cardBg, borderColor: theme.borderColor }}>
                <p className="text-xs font-semibold" style={{ color: theme.linkColor }}>FRUITS</p>
                <p className="font-bold" style={{ color: theme.primaryText }}>Fresh Apples</p>
                <p className="text-sm font-bold" style={{ color: theme.linkColor }}>Rs. 450</p>
                <button
                  type="button"
                  className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold"
                  style={{ backgroundColor: theme.buttonBg, color: theme.buttonText }}
                >
                  Add to Cart
                </button>
              </div>
            </div>

            <div className="px-4 py-3 text-xs text-center" style={{ backgroundColor: theme.footerBg, color: theme.footerText }}>
              © 2026 NAVIROSE. All rights reserved.
            </div>
          </div>
          <p className="text-xs text-pink-600 mt-2">
            💡 Color change karte hi preview update hota hai. Save dabayein toh website par bhi apply hoga.
          </p>
        </div>

        {/* Backgrounds */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">🎨 Backgrounds</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ColorInput label="Body Background" field="bodyBg" description="Main page background" />
            <ColorInput label="Card Background" field="cardBg" description="Products, cards" />
            <ColorInput label="Header Background" field="headerBg" description="Top navigation" />
            <ColorInput label="Top Bar Background" field="topBarBg" description="Contact info bar" />
            <ColorInput label="Footer Background" field="footerBg" description="Bottom footer" />
          </div>
        </div>

        {/* Text */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">📝 Text Colors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ColorInput label="Primary Text" field="primaryText" description="Headings" />
            <ColorInput label="Secondary Text" field="secondaryText" description="Descriptions" />
            <ColorInput label="Header Text" field="headerText" description="Logo/name" />
            <ColorInput label="Top Bar Text" field="topBarText" />
            <ColorInput label="Footer Text" field="footerText" />
          </div>
        </div>

        {/* Buttons */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">🔘 Buttons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ColorInput label="Button Background" field="buttonBg" description="Primary buttons" />
            <ColorInput label="Button Text" field="buttonText" />
            <ColorInput label="Button Hover" field="buttonHoverBg" />
          </div>
        </div>

        {/* Accent */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">✨ Accent & Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ColorInput label="Accent Color" field="accentColor" />
            <ColorInput label="Link Color" field="linkColor" />
            <ColorInput label="Badge Color" field="badgeColor" />
            <ColorInput label="Border Color" field="borderColor" />
          </div>
        </div>

        {/* Bottom Nav */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">📱 Mobile Bottom Navigation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ColorInput label="Nav Background" field="bottomNavBg" />
            <ColorInput label="Active Background" field="bottomNavActiveBg" />
            <ColorInput label="Active Text" field="bottomNavActiveText" />
            <ColorInput label="Inactive Text" field="bottomNavInactiveText" />
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end sticky bottom-4">
          <button
            type="submit"
            disabled={saving}
            className={`text-white py-3 px-10 rounded-lg font-semibold transition flex items-center gap-2 shadow-xl ${
              saving ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            style={{ backgroundColor: '#F5AEC0' }}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Pink Theme'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default ThemeSettings;